import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import dataManager from '../utils/dataManager';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';
import './PhilosophyItem.css';

const PhilosophyItem = ({ philosophyName }) => {
  const { user } = useAuth();
  const [reflection, setReflection] = useState('');
  const [communityReflections, setCommunityReflections] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [philosophies, setPhilosophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 加载哲学数据
  useEffect(() => {
    const loadPhilosophies = async () => {
      try {
        setLoading(true);
        setError(null);
        const philosophiesData = await dataManager.getPracticePhilosophy();
        setPhilosophies(Array.isArray(philosophiesData) ? philosophiesData : []);
      } catch (err) {
        console.error('Error loading philosophies:', err);
        setError('加载哲学原则失败，请确保Django服务器正在运行');
        // 降级到本地数据
        const fallbackData = dataManager.getPracticePhilosophySync() || [];
        setPhilosophies(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    loadPhilosophies();
  }, []);

  // 根据名称找到对应的哲学项目
  const philosophy = philosophies.find(p =>
    p && p.name && p.name.toLowerCase().replace(/\s+/g, '-') === philosophyName
  );

  // 加载社区反思数据
  useEffect(() => {
    const loadCommunityReflections = async () => {
      if (!philosophy) return;

      try {
        if (ConfigService.useServerData()) {
          const reflections = await dataManager.getPhilosophyReflectionsByName(philosophy.name);
          setCommunityReflections(Array.isArray(reflections) ? reflections.map(formatPhilosophyReflection) : []);
        } else {
          const allReflections = dataManager.getPhilosophyReflections();
          const philosophyReflections = allReflections[philosophy.name] || [];
          setCommunityReflections(philosophyReflections);
        }
      } catch (err) {
        console.error('Error loading philosophy reflections:', err);
        setCommunityReflections([]);
      }
    };

    loadCommunityReflections();
  }, [philosophy]);

  if (!philosophy) {
    return (
      <div className="philosophy-item">
        <div className="philosophy-header">
          <h2>🌟 练习哲学</h2>
          <p>未找到对应的哲学内容</p>
        </div>
      </div>
    );
  }

  const formatPhilosophyReflection = (item) => ({
    id: item.id,
    content: item.content,
    author: item.author,
    date: item.created_at || item.date,
    likes: item.likes_count ?? item.likes ?? 0,
    tags: item.tags || [],
    isLiked: item.is_liked || false,
  });

  const getErrorMessage = (data, fallback) => {
    if (!data || typeof data !== 'object') return fallback;
    if (data.detail) return data.detail;
    if (data.non_field_errors?.[0]) return data.non_field_errors[0];

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  const handleAddCommunityReflection = async () => {
    if (!user) {
      alert('请先登录才能添加反思');
      return;
    }

    if (!reflection.trim()) {
      alert('请输入反思内容');
      return;
    }

    try {
      setSubmitting(true);

      if (ConfigService.useServerData()) {
        const response = await fetch(ConfigService.getApiUrl(API_ENDPOINTS.PHILOSOPHY_REFLECTIONS), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            philosophy_id: philosophy.id,
            content: reflection.trim(),
            tags: [],
          }),
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getErrorMessage(responseData, '分享感悟失败'));
        }

        const refreshedReflections = await dataManager.getPhilosophyReflectionsByName(philosophy.name);
        setCommunityReflections(
          Array.isArray(refreshedReflections)
            ? refreshedReflections.map(formatPhilosophyReflection)
            : []
        );
      } else {
        const newReflection = {
          id: Date.now(),
          content: reflection.trim(),
          author: user.username,
          date: new Date().toISOString().split('T')[0],
          likes: 0
        };

        const updatedReflections = [newReflection, ...communityReflections];
        setCommunityReflections(updatedReflections);

        // 保存到localStorage
        const allReflections = dataManager.getPhilosophyReflections();
        const updatedAllReflections = {
          ...allReflections,
          [philosophy.name]: updatedReflections
        };
        localStorage.setItem('philosophyReflections', JSON.stringify(updatedAllReflections));
      }

      setReflection('');
      setShowAddForm(false);
      alert('反思已分享到社区！');
    } catch (err) {
      console.error('Error saving philosophy reflection:', err);
      alert(err.message || '分享感悟失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = (reflectionId) => {
    const updatedReflections = communityReflections.map(r =>
      r.id === reflectionId ? { ...r, likes: r.likes + 1 } : r
    );
    setCommunityReflections(updatedReflections);

    // 保存到localStorage
    const allReflections = dataManager.getPhilosophyReflections();
    const updatedAllReflections = {
      ...allReflections,
      [philosophy.name]: updatedReflections
    };
    localStorage.setItem('philosophyReflections', JSON.stringify(updatedAllReflections));
  };

  return (
    <div className="philosophy-item">
      <div className="philosophy-header">
        <div className="philosophy-icon-large">
          <span className="icon">{philosophy.icon}</span>
        </div>
        <h2>{philosophy.name}</h2>
        <p className="philosophy-subtitle">{philosophy.description}</p>
      </div>

      <div className="philosophy-content">
        <div className="detailed-content">
          <h3>📖 深入理解</h3>
          <div className="details-text">
            {philosophy.details ? philosophy.details.split('\n\n').map((paragraph, pIndex) => (
              <p key={pIndex}>{paragraph}</p>
            )) : null}
          </div>
        </div>

        {philosophy.practices && Array.isArray(philosophy.practices) && (
          <div className="practice-suggestions">
            <h3>🎯 实践建议</h3>
            <ul>
              {philosophy.practices.map((practice, pIndex) => (
                <li key={pIndex}>{practice}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="reflection-section">
          <h3>💭 社区反思</h3>
          <div className="reflection-prompt">
            <p>分享您对"{philosophy.name}"的理解和感悟：</p>

            {user && (
              <div className="add-reflection-controls">
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="toggle-form-btn"
                >
                  {showAddForm ? '取消分享' : '分享我的感悟'}
                </button>
              </div>
            )}

            {!user && (
              <div className="login-prompt">
                <p>登录后可以分享您的感悟</p>
              </div>
            )}

            {showAddForm && user && (
              <div className="add-reflection-form">
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="写下你的思考和感悟..."
                  className="reflection-textarea"
                  rows="4"
                />
                <div className="form-actions">
                  <button
                    onClick={handleAddCommunityReflection}
                    className="save-reflection-btn"
                    disabled={submitting || !reflection.trim()}
                  >
                    {submitting ? '分享中...' : '分享到社区'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setReflection('');
                    }}
                    className="cancel-btn"
                  >
                    取消
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 社区反思列表 */}
          <div className="community-reflections">
            <h4>🌟 社区分享</h4>
            {communityReflections.length > 0 ? (
              <div className="reflections-list">
                {communityReflections.map((refl) => (
                  <div key={refl.id} className="reflection-item">
                    <div className="reflection-header">
                      <div className="author-info">
                        <div className="author-avatar">
                          {getAuthorAvatar(refl.author)}
                        </div>
                        <div className="author-details">
                          <span className="author-name">
                            {typeof refl.author === 'object' ?
                              (refl.author.name || refl.author.username || '匿名用户') :
                              refl.author || '匿名用户'}
                          </span>
                          <span className="reflection-date">{formatDate(refl.date)}</span>
                        </div>
                      </div>
                      <div className="reflection-actions">
                        <button
                          onClick={() => handleLike(refl.id)}
                          className="like-btn"
                        >
                          ❤️ {refl.likes}
                        </button>
                      </div>
                    </div>
                    <div className="reflection-content">
                      {refl.content}
                    </div>
                    {refl.tags && Array.isArray(refl.tags) && (
                      <div className="reflection-tags">
                        {refl.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-reflections">
                <div className="empty-icon">💭</div>
                <h5>还没有社区分享</h5>
                <p>
                  {user
                    ? '成为第一个分享感悟的人吧！'
                    : '登录后可以分享您的理解。'
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="philosophy-footer">
          <div className="wisdom-quote">
            <h3>🕉️ 相关智慧</h3>
            <blockquote>
              {getWisdomQuote(philosophy.name)}
            </blockquote>
          </div>
          
          <div className="related-practices">
            <h3>🔗 相关练习</h3>
            <div className="practice-links">
              <a href="#" className="practice-link">
                📖 阅读更多关于{philosophy.name}
              </a>
              <a href="#" className="practice-link">
                🎥 观看相关视频
              </a>
              <a href="#" className="practice-link">
                🧘‍♀️ 开始练习
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 根据哲学名称获取相关的智慧引言
const getWisdomQuote = (philosophyName) => {
  const quotes = {
    'Find your breath': '"呼吸是生命的桥梁，连接身体与心灵，连接有意识与无意识，连接自我与宇宙。" — Thich Nhat Hanh',
    'Trust the process': '"耐心是一切的关键。当你种下一颗种子时，你不会每天挖出来看它是否在生长。" — Rumi',
    'Practice with love': '"爱是唯一的老师，它远比责任感更有价值。" — 爱因斯坦',
    'Embrace the journey': '"道路就是目标。每一步都是到达，每一步都是出发。" — 老子'
  };

  return quotes[philosophyName] || '"瑜伽是心灵波动的止息。" — 《瑜伽经》';
};

// 生成作者头像
const getAuthorAvatar = (author) => {
  // 确保 authorName 是字符串
  const authorName = typeof author === 'object' ?
    (author.name || author.username || '匿名') :
    (author || '匿名');

  const firstChar = authorName.charAt(0);
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
  const colorIndex = authorName.length % colors.length;

  return (
    <div
      className="avatar-circle"
      style={{ backgroundColor: colors[colorIndex] }}
    >
      {firstChar}
    </div>
  );
};

// 格式化日期
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return '昨天';
  if (diffDays === 2) return '前天';
  if (diffDays <= 7) return `${diffDays}天前`;

  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric'
  });
};

export default PhilosophyItem;
